import React, { useMemo, useState, useCallback } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import multiMonthPlugin from "@fullcalendar/multimonth";
import interactionPlugin from "@fullcalendar/interaction";
import {
  Box,
  Button,
  Typography,
  Paper,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  NavigateBefore,
  NavigateNext,
  Refresh as RefreshIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { DateSelectArg, EventClickArg } from "@fullcalendar/core";
import { observer } from "mobx-react";
import moment from "moment";

import { PublicHolidayDto, CreatePublicHolidayDto } from "../../../api-client";
import { usePublicHolidays } from "../../../stores/PublicHoliday/publicHoliday.queries";
import {
  useCreatePublicHoliday,
  useCreatePublicHolidaysBulk,
  useDeletePublicHoliday,
  useDeletePublicHolidaysByYear,
} from "../../../stores/PublicHoliday/publicHoliday.mutations";
import {
  readICalFile,
  isValidICalFile,
  convertToPublicHolidayDtos,
  ParsedICalEvent,
} from "../../../utils/icalParser";
import useStore from "../../../helpers/useStore";
import { UserContext } from "../../../stores/User/User.provider";
import notificationStore from "../../../stores/Notification/notificationStore";
import { mobiliTheme } from "../../../themes/mobiliTheme";
import DatePicker from "../../../atoms/DatePicker";

// German bundesland mapping
const BUNDESLAND_OPTIONS = [
  { code: "Baden-Württemberg", name: "Baden-Württemberg" },
  { code: "Bayern", name: "Bayern" },
  { code: "Berlin", name: "Berlin" },
  { code: "Brandenburg", name: "Brandenburg" },
  { code: "Bremen", name: "Bremen" },
  { code: "Hamburg", name: "Hamburg" },
  { code: "Hessen", name: "Hessen" },
  { code: "Mecklenburg-Vorpommern", name: "Mecklenburg-Vorpommern" },
  { code: "Niedersachsen", name: "Niedersachsen" },
  { code: "Nordrhein-Westfalen", name: "Nordrhein-Westfalen" },
  { code: "Rheinland-Pfalz", name: "Rheinland-Pfalz" },
  { code: "Saarland", name: "Saarland" },
  { code: "Sachsen", name: "Sachsen" },
  { code: "Sachsen-Anhalt", name: "Sachsen-Anhalt" },
  { code: "Schleswig-Holstein", name: "Schleswig-Holstein" },
  { code: "Thüringen", name: "Thüringen" },
];

interface CalendarEvent {
  id: string;
  title: string;
  start: string;
  end: string;
  backgroundColor: string;
  borderColor: string;
  extendedProps: {
    holiday: PublicHolidayDto;
  };
}

const PublicHolidayCalendar: React.FC = observer(() => {
  const UserStore = useStore(UserContext);
  const { user } = UserStore;

  // Get bundesland from user's business
  const userBundesland = user?.business?.bundesland || "";

  // State
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedBundesland, setSelectedBundesland] = useState<string>(userBundesland);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [deleteYearDialogOpen, setDeleteYearDialogOpen] = useState(false);
  const [deleteBundeslandDialogOpen, setDeleteBundeslandDialogOpen] = useState(false);
  const [deleteHolidayDialog, setDeleteHolidayDialog] = useState<PublicHolidayDto | null>(null);
  const [pendingImport, setPendingImport] = useState<CreatePublicHolidayDto[]>([]);
  const [parsedEvents, setParsedEvents] = useState<ParsedICalEvent[]>([]);
  const [importYear, setImportYear] = useState<number>(selectedYear);
  const [importBundesland, setImportBundesland] = useState<string>(selectedBundesland || userBundesland);
  
  // Create holiday dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newHolidayTitle, setNewHolidayTitle] = useState("");
  const [newHolidayStart, setNewHolidayStart] = useState("");
  const [newHolidayEnd, setNewHolidayEnd] = useState("");

  // Queries
  const {
    data: holidays = [],
    isLoading,
    refetch,
  } = usePublicHolidays(selectedYear, selectedBundesland || undefined, true);

  // Mutations
  const createMutation = useCreatePublicHoliday();
  const createBulkMutation = useCreatePublicHolidaysBulk();
  const deleteHolidayMutation = useDeletePublicHoliday();
  const deleteByYearMutation = useDeletePublicHolidaysByYear();

  // Recompute pending import DTOs when importBundesland or importYear changes
  const importHolidays = useMemo(() => {
    if (parsedEvents.length === 0) return pendingImport;
    return convertToPublicHolidayDtos(parsedEvents, importBundesland, importYear);
  }, [parsedEvents, importBundesland, importYear, pendingImport]);

  // Convert holidays to calendar events
  const calendarEvents: CalendarEvent[] = useMemo(() => {
    return holidays.map((holiday: PublicHolidayDto) => ({
      id: String(holiday.id),
      title: holiday.title,
      start: holiday.start,
      end: moment(holiday.end).add(1, "day").format("YYYY-MM-DD"), // FullCalendar end is exclusive
      backgroundColor: mobiliTheme.palette.error.main,
      borderColor: mobiliTheme.palette.error.dark,
      extendedProps: {
        holiday,
      },
    }));
  }, [holidays]);

  // Handle year navigation
  const handlePreviousYear = useCallback(() => {
    setSelectedYear((prev) => prev - 1);
  }, []);

  const handleNextYear = useCallback(() => {
    setSelectedYear((prev) => prev + 1);
  }, []);

  // Handle date selection (click or drag to select range)
  const handleDateSelect = useCallback((selectInfo: DateSelectArg) => {
    const startDate = moment(selectInfo.start).format("YYYY-MM-DD");
    // FullCalendar's end is exclusive, so subtract 1 day for the actual end date
    const endDate = moment(selectInfo.end).subtract(1, "day").format("YYYY-MM-DD");
    
    setNewHolidayTitle("");
    setNewHolidayStart(startDate);
    setNewHolidayEnd(endDate);
    setCreateDialogOpen(true);
  }, []);

  // Handle create holiday
  const handleCreateHoliday = useCallback(async () => {
    if (!newHolidayTitle.trim()) {
      notificationStore.showMessage("Bitte geben Sie einen Titel ein", "warning");
      return;
    }

    const bundeslandToUse = selectedBundesland || userBundesland;
    const year = moment(newHolidayStart).year();

    try {
      await createMutation.mutateAsync({
        title: newHolidayTitle.trim(),
        start: newHolidayStart,
        end: newHolidayEnd,
        bundesland: bundeslandToUse || undefined,
        year,
      });
      setCreateDialogOpen(false);
      setNewHolidayTitle("");
      setNewHolidayStart("");
      setNewHolidayEnd("");
      refetch();
    } catch (error) {
      console.error("Error creating holiday:", error);
    }
  }, [newHolidayTitle, newHolidayStart, newHolidayEnd, selectedBundesland, userBundesland, createMutation, refetch]);

  // Open create dialog manually (for add button)
  const handleOpenCreateDialog = useCallback(() => {
    const today = moment().format("YYYY-MM-DD");
    setNewHolidayTitle("");
    setNewHolidayStart(today);
    setNewHolidayEnd(today);
    setCreateDialogOpen(true);
  }, []);

  // Handle event click
  const handleEventClick = useCallback((clickInfo: EventClickArg) => {
    const holiday = clickInfo.event.extendedProps.holiday as PublicHolidayDto;
    setDeleteHolidayDialog(holiday);
  }, []);

  // Handle iCal file import
  const handleFileImport = useCallback(async () => {
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = ".ics,.ical,.icalendar";

    fileInput.onchange = async (event: Event) => {
      const target = event.target as HTMLInputElement;
      const file = target.files?.[0];

      if (!file) return;

      if (!isValidICalFile(file)) {
        notificationStore.showMessage(
          "Bitte wählen Sie eine gültige iCal-Datei (.ics)",
          "error"
        );
        return;
      }

      try {
        const parsedEventsResult = await readICalFile(file);
        
        if (parsedEventsResult.length === 0) {
          notificationStore.showMessage(
            "Keine Ereignisse in der Datei gefunden",
            "warning"
          );
          return;
        }

        // Sync the import year/bundesland to the currently selected values so the
        // import matches the year the user is actually viewing.
        const yearToImport = selectedYear;
        const bundeslandToImport = selectedBundesland || userBundesland;
        setImportYear(yearToImport);
        setImportBundesland(bundeslandToImport);

        // Check if there are events for the selected year
        const dtos = convertToPublicHolidayDtos(parsedEventsResult, bundeslandToImport, yearToImport);

        if (dtos.length === 0) {
          notificationStore.showMessage(
            `Keine Feiertage für das Jahr ${yearToImport} gefunden`,
            "warning"
          );
          return;
        }

        setParsedEvents(parsedEventsResult);
        setPendingImport(dtos);
        setImportDialogOpen(true);
      } catch (error) {
        console.error("Error parsing iCal file:", error);
        notificationStore.showMessage(
          "Fehler beim Lesen der iCal-Datei",
          "error",
          error
        );
      }
    };

    fileInput.click();
  }, [selectedYear, selectedBundesland, userBundesland]);

  // Confirm import
  const handleConfirmImport = useCallback(async () => {
    try {
      await createBulkMutation.mutateAsync(importHolidays);
      setImportDialogOpen(false);
      setPendingImport([]);
      setParsedEvents([]);
      refetch();
    } catch (error) {
      console.error("Error importing holidays:", error);
    }
  }, [createBulkMutation, importHolidays, refetch]);

  // Delete single holiday
  const handleDeleteHoliday = useCallback(async () => {
    if (!deleteHolidayDialog) return;

    try {
      await deleteHolidayMutation.mutateAsync(deleteHolidayDialog.id);
      setDeleteHolidayDialog(null);
      refetch();
    } catch (error) {
      console.error("Error deleting holiday:", error);
    }
  }, [deleteHolidayDialog, deleteHolidayMutation, refetch]);

  // Delete all holidays for year
  const handleDeleteYear = useCallback(async () => {
    try {
      await deleteByYearMutation.mutateAsync({ year: selectedYear });
      setDeleteYearDialogOpen(false);
      refetch();
    } catch (error) {
      console.error("Error deleting holidays for year:", error);
    }
  }, [selectedYear, deleteByYearMutation, refetch]);

  // Delete all holidays for selected bundesland
  const handleDeleteBundesland = useCallback(async () => {
    if (!selectedBundesland) return;

    try {
      await deleteByYearMutation.mutateAsync({ year: selectedYear, bundesland: selectedBundesland });
      setDeleteBundeslandDialogOpen(false);
      refetch();
    } catch (error) {
      console.error("Error deleting holidays for bundesland:", error);
    }
  }, [selectedBundesland, selectedYear, deleteByYearMutation, refetch]);

  const bundeslandName = useMemo(() => {
    return BUNDESLAND_OPTIONS.find((b) => b.code === selectedBundesland)?.name || selectedBundesland;
  }, [selectedBundesland]);

  const handleEventContent = useCallback((eventInfo: any) => {
    return (
      <Tooltip title={eventInfo.event.title}>
        <div>{eventInfo.event.title}</div>
      </Tooltip>
    );
  }, []);

  return (
    <Box sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="h5">Feiertage</Typography>
            
            {/* Year Navigation */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton onClick={handlePreviousYear} size="small">
                <NavigateBefore />
              </IconButton>
              <Typography variant="h6" sx={{ minWidth: 60, textAlign: "center" }}>
                {selectedYear}
              </Typography>
              <IconButton onClick={handleNextYear} size="small">
                <NavigateNext />
              </IconButton>
            </Box>

            {/* Bundesland Selector */}
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Bundesland</InputLabel>
              <Select
                value={selectedBundesland}
                label="Bundesland"
                onChange={(e) => setSelectedBundesland(e.target.value)}
              >
                <MenuItem value="">
                  <em>Alle</em>
                </MenuItem>
                {BUNDESLAND_OPTIONS.map((bl) => (
                  <MenuItem key={bl.code} value={bl.code}>
                    {bl.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Actions */}
          <Box sx={{ display: "flex", gap: 1 }}>
            <Tooltip title="Aktualisieren">
              <IconButton onClick={() => refetch()} disabled={isLoading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteBundeslandDialogOpen(true)}
              disabled={holidays.length === 0 || !selectedBundesland}
            >
              Bundesland löschen
            </Button>

            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteYearDialogOpen(true)}
              disabled={holidays.length === 0}
            >
              Jahr löschen
            </Button>

            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              onClick={handleFileImport}
            >
              iCal importieren
            </Button>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenCreateDialog}
            >
              Feiertag hinzufügen
            </Button>
          </Box>
        </Box>

        {/* Info */}
        {userBundesland && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Standard-Bundesland aus Ihrem Unternehmen: {bundeslandName || userBundesland}
          </Typography>
        )}
      </Paper>

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Calendar */}
      {!isLoading && (
        <Paper sx={{ flex: 1, p: 2, overflow: "auto" }}>
          <FullCalendar
            locale="de"
            plugins={[dayGridPlugin, multiMonthPlugin, interactionPlugin]}
            initialView="multiMonthYear"
            initialDate={`${selectedYear}-01-01`}
            key={selectedYear} // Force re-render when year changes
            headerToolbar={false}
            events={calendarEvents}
            eventClick={handleEventClick}
            eventContent={handleEventContent}
            selectable={true}
            select={handleDateSelect}
            selectMirror={true}
            eventDisplay="block"
            dayMaxEvents={false}
            height="auto"
            firstDay={1}
            buttonText={{
              today: "Heute",
              month: "Monat",
              year: "Jahr",
            }}
            schedulerLicenseKey="0067980715-fcs-1697454050"
          />
        </Paper>
      )}

      {/* Import Confirmation Dialog */}
      <Dialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Feiertage importieren</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            {!importBundesland && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Kein Bundesland ausgewählt! Die Feiertage werden für <strong>alle Bundesländer</strong> importiert.
              </Alert>
            )}
            <Alert severity="info" sx={{ mb: 2 }}>
              {importHolidays.length} Feiertag(e) werden für das Jahr{" "}
              <strong>{importYear}</strong> und Bundesland{" "}
              <strong>{importBundesland || "National (alle)"}</strong> importiert.
            </Alert>

            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <TextField
                type="number"
                label="Jahr"
                value={importYear}
                onChange={(e) => setImportYear(parseInt(e.target.value, 10))}
                size="small"
              />
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Bundesland</InputLabel>
                <Select
                  value={importBundesland}
                  label="Bundesland"
                  onChange={(e) => setImportBundesland(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Alle (National)</em>
                  </MenuItem>
                  {BUNDESLAND_OPTIONS.map((bl) => (
                    <MenuItem key={bl.code} value={bl.code}>
                      {bl.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>

          <TableContainer sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Titel</TableCell>
                  <TableCell>Startdatum</TableCell>
                  <TableCell>Enddatum</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {importHolidays.map((holiday, index) => (
                  <TableRow key={index}>
                    <TableCell>{holiday.title}</TableCell>
                    <TableCell>
                      {moment(holiday.start).format("DD.MM.YYYY")}
                    </TableCell>
                    <TableCell>
                      {moment(holiday.end).format("DD.MM.YYYY")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)}>Abbrechen</Button>
          <Button
            onClick={handleConfirmImport}
            variant="contained"
            disabled={createBulkMutation.isPending}
          >
            {createBulkMutation.isPending ? (
              <CircularProgress size={20} />
            ) : (
              "Importieren"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Holiday Dialog */}
      <Dialog
        open={deleteHolidayDialog !== null}
        onClose={() => setDeleteHolidayDialog(null)}
      >
        <DialogTitle>Feiertag löschen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Möchten Sie den Feiertag &quot;{deleteHolidayDialog?.title}&quot; wirklich
            löschen?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteHolidayDialog(null)}>Abbrechen</Button>
          <Button
            onClick={handleDeleteHoliday}
            color="error"
            variant="contained"
            disabled={deleteHolidayMutation.isPending}
          >
            {deleteHolidayMutation.isPending ? (
              <CircularProgress size={20} />
            ) : (
              "Löschen"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Bundesland Dialog */}
      <Dialog
        open={deleteBundeslandDialogOpen}
        onClose={() => setDeleteBundeslandDialogOpen(false)}
      >
        <DialogTitle>Feiertage für {bundeslandName} in {selectedYear} löschen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Möchten Sie wirklich alle {holidays.length} Feiertag(e) für{" "}
            <strong>{bundeslandName}</strong> im Jahr {selectedYear} löschen? Diese Aktion kann nicht rückgängig gemacht
            werden.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteBundeslandDialogOpen(false)}>Abbrechen</Button>
          <Button
            onClick={handleDeleteBundesland}
            color="error"
            variant="contained"
            disabled={deleteByYearMutation.isPending}
          >
            {deleteByYearMutation.isPending ? (
              <CircularProgress size={20} />
            ) : (
              "Alle löschen"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Year Dialog */}
      <Dialog
        open={deleteYearDialogOpen}
        onClose={() => setDeleteYearDialogOpen(false)}
      >
        <DialogTitle>Alle Feiertage für {selectedYear} löschen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Möchten Sie wirklich alle {holidays.length} Feiertag(e) für das Jahr{" "}
            {selectedYear} löschen? Diese Aktion kann nicht rückgängig gemacht
            werden.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteYearDialogOpen(false)}>Abbrechen</Button>
          <Button
            onClick={handleDeleteYear}
            color="error"
            variant="contained"
            disabled={deleteByYearMutation.isPending}
          >
            {deleteByYearMutation.isPending ? (
              <CircularProgress size={20} />
            ) : (
              "Alle löschen"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Holiday Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Feiertag hinzufügen</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="Titel"
              value={newHolidayTitle}
              onChange={(e) => setNewHolidayTitle(e.target.value)}
              fullWidth
              required
              autoFocus
              placeholder="z.B. Weihnachten, Neujahr"
            />
            <DatePicker
              name="start"
              label="Startdatum"
              value={newHolidayStart}
              onChange={(e: any) => {
                const val = e.target.value;
                if (val) {
                  setNewHolidayStart(moment(val).format("YYYY-MM-DD"));
                }
              }}
              required
              floatingLabel
              style={{ width: "100%" }}
            />
            <DatePicker
              name="end"
              label="Enddatum"
              value={newHolidayEnd}
              onChange={(e: any) => {
                const val = e.target.value;
                if (val) {
                  setNewHolidayEnd(moment(val).format("YYYY-MM-DD"));
                }
              }}
              required
              floatingLabel
              minDate={newHolidayStart}
              style={{ width: "100%" }}
            />
            <Alert severity="info">
              Bundesland: <strong>{bundeslandName || "National (alle)"}</strong>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Abbrechen</Button>
          <Button
            onClick={handleCreateHoliday}
            variant="contained"
            disabled={createMutation.isPending || !newHolidayTitle.trim()}
          >
            {createMutation.isPending ? (
              <CircularProgress size={20} />
            ) : (
              "Erstellen"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export default PublicHolidayCalendar;